import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { api, errorMessage } from '@/lib/api';
import { emptyWeek, HoursGrid, validateWeek, weekFromRows, weekToIntervals, type HourRow, type Week } from './hours-grid';

const useHours = (branchId: string, barberId?: string) =>
  useQuery({
    queryKey: ['org', 'hours', branchId, barberId ?? 'branch'],
    queryFn: () => api.get<HourRow[]>('/org/hours', { branchId, barberId }),
  });

/**
 * Editor da grade semanal de uma unidade ou, com `barberId`, do horário individual do profissional.
 * Sem horário próprio, vale o da unidade.
 */
export function HoursEditor({ branchId, barberId }: { branchId: string; barberId?: string }) {
  const qc = useQueryClient();
  const hours = useHours(branchId, barberId);
  const unit = useHours(branchId);
  const [week, setWeek] = useState<Week>(emptyWeek());
  const [custom, setCustom] = useState(true);
  const saved = useMemo(() => (hours.data ? JSON.stringify(weekFromRows(hours.data)) : ''), [hours.data]);

  useEffect(() => {
    if (!hours.data) return;
    setWeek(weekFromRows(hours.data));
    setCustom(!barberId || hours.data.length > 0);
  }, [hours.data, barberId]);

  const errors = validateWeek(week);
  const dirty = JSON.stringify(week) !== saved;

  const save = useMutation({
    mutationFn: (w: Week) => api.put('/org/hours', { branchId, barberId: barberId ?? null, intervals: weekToIntervals(w) }),
    onSuccess: () => {
      toast.success('Horário salvo');
      qc.invalidateQueries({ queryKey: ['org', 'hours'] });
      qc.invalidateQueries({ queryKey: ['org', 'onboarding'] });
      qc.invalidateQueries({ queryKey: ['agenda'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (hours.isLoading) return <LoadingState />;
  if (hours.error) return <ErrorState error={hours.error} onRetry={() => hours.refetch()} />;

  if (barberId && !custom) {
    return (
      <div className="grid gap-4">
        <div className="rounded-lg border bg-muted/40 p-4 text-sm">
          <p className="font-medium">Este profissional segue o horário da unidade.</p>
          <p className="mt-1 text-muted-foreground">Defina um horário próprio só se ele trabalha em dias ou horas diferentes. Dias e horas fora do horário individual ficam indisponíveis para ele.</p>
        </div>
        {unit.data && <HoursGrid value={weekFromRows(unit.data)} onChange={() => undefined} errors={{}} disabled />}
        <div>
          <Button
            onClick={() => {
              setWeek(unit.data ? weekFromRows(unit.data) : emptyWeek());
              setCustom(true);
            }}
          >
            Definir horário próprio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {barberId && (
        <p className="text-sm text-muted-foreground">Com horário próprio, vale só este. Para voltar a seguir a unidade, remova o horário individual.</p>
      )}
      <HoursGrid value={week} onChange={setWeek} errors={errors} />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => save.mutate(week)} disabled={!dirty || save.isPending || Object.keys(errors).length > 0}>
          {save.isPending ? 'Salvando…' : 'Salvar horário'}
        </Button>
        {dirty && (
          <Button variant="outline" onClick={() => setWeek(hours.data ? weekFromRows(hours.data) : emptyWeek())}>
            Descartar alterações
          </Button>
        )}
        {barberId && (
          <ConfirmDialog
            trigger={<Button variant="ghost">Voltar ao horário da unidade</Button>}
            title="Remover o horário individual?"
            description="O profissional passa a seguir o horário de funcionamento da unidade."
            confirmLabel="Remover horário individual"
            onConfirm={async () => {
              await save.mutateAsync(emptyWeek());
              setCustom(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
